export type LanguageOption = {
  code: string;
  englishName: string;
  nativeName: string;
  rtl?: boolean;
};

export const UI_LANGUAGE_STORAGE_KEY = "tihany-ui-language";

export const CHAT_LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: "hu", englishName: "Hungarian", nativeName: "Magyar" },
  { code: "en", englishName: "English", nativeName: "English" },
  { code: "de", englishName: "German", nativeName: "Deutsch" },
  { code: "pl", englishName: "Polish", nativeName: "Polski" },
  { code: "he", englishName: "Hebrew", nativeName: "עברית", rtl: true },
  { code: "zh", englishName: "Chinese", nativeName: "中文" },
  { code: "cs", englishName: "Czech", nativeName: "Čeština" },
  { code: "sk", englishName: "Slovak", nativeName: "Slovenčina" },
  { code: "nl", englishName: "Dutch", nativeName: "Nederlands" },
  { code: "fr", englishName: "French", nativeName: "Français" },
  { code: "it", englishName: "Italian", nativeName: "Italiano" },
  { code: "es", englishName: "Spanish", nativeName: "Español" },
  { code: "ro", englishName: "Romanian", nativeName: "Română" },
  { code: "ar", englishName: "Arabic", nativeName: "العربية", rtl: true },
  { code: "ko", englishName: "Korean", nativeName: "한국어" },
  { code: "ja", englishName: "Japanese", nativeName: "日本語" },
  { code: "da", englishName: "Danish", nativeName: "Dansk" },
  { code: "fi", englishName: "Finnish", nativeName: "Suomi" },
  { code: "no", englishName: "Norwegian", nativeName: "Norsk" },
  { code: "sv", englishName: "Swedish", nativeName: "Svenska" },
  { code: "hr", englishName: "Croatian", nativeName: "Hrvatski" },
  { code: "sr", englishName: "Serbian", nativeName: "Srpski" },
  { code: "sl", englishName: "Slovenian", nativeName: "Slovenščina" },
  { code: "ru", englishName: "Russian", nativeName: "Русский" },
  { code: "uk", englishName: "Ukrainian", nativeName: "Українська" },
  { code: "tr", englishName: "Turkish", nativeName: "Türkçe" },
  { code: "la", englishName: "Latin", nativeName: "Latina" },
  { code: "pt", englishName: "Portuguese", nativeName: "Português" },
  { code: "el", englishName: "Greek", nativeName: "Ελληνικά" },
  { code: "bg", englishName: "Bulgarian", nativeName: "Български" },
  { code: "hi", englishName: "Hindi", nativeName: "हिन्दी" },
  { code: "ka", englishName: "Georgian", nativeName: "ქართული" },
  { code: "fa", englishName: "Persian", nativeName: "فارسی", rtl: true },
];

export const CHAT_LANGUAGE_CODES = CHAT_LANGUAGE_OPTIONS.map((option) => option.code);

const RTL_LANGUAGES = new Set(CHAT_LANGUAGE_OPTIONS.filter((option) => option.rtl).map((option) => option.code));
const SUPPORTED_SET = new Set(CHAT_LANGUAGE_CODES);

const LANGUAGE_ALIAS_MAP: Record<string, string> = {
  iw: "he",
  nb: "no",
  nn: "no",
  zh_cn: "zh",
  zh_tw: "zh",
  zh_hk: "zh",
  zh_hans: "zh",
  zh_hant: "zh",
  fa_ir: "fa",
  fa_af: "fa",
};

export const normalizeLanguageCode = (value: string | null | undefined) => {
  if (!value) return "";
  const cleaned = value.toLowerCase().trim().replace(/-/g, "_");
  if (!cleaned) return "";
  if (LANGUAGE_ALIAS_MAP[cleaned]) return LANGUAGE_ALIAS_MAP[cleaned];
  const base = cleaned.split("_")[0];
  if (LANGUAGE_ALIAS_MAP[base]) return LANGUAGE_ALIAS_MAP[base];
  return base;
};

export const isSupportedLanguageCode = (code: string | null | undefined) => {
  const normalized = normalizeLanguageCode(code);
  return !!normalized && SUPPORTED_SET.has(normalized);
};

export const isRtlLanguage = (code: string | null | undefined) => {
  const normalized = normalizeLanguageCode(code);
  return !!normalized && RTL_LANGUAGES.has(normalized);
};

export const getLanguageOption = (code: string | null | undefined) => {
  const normalized = normalizeLanguageCode(code);
  return CHAT_LANGUAGE_OPTIONS.find((option) => option.code === normalized) ?? null;
};

export const pickSupportedLanguage = (
  candidates: Array<string | null | undefined>,
  fallback = "en",
) => {
  for (const candidate of candidates) {
    const normalized = normalizeLanguageCode(candidate);
    if (normalized && SUPPORTED_SET.has(normalized)) {
      return normalized;
    }
  }
  return isSupportedLanguageCode(fallback) ? normalizeLanguageCode(fallback) : "en";
};

export const detectBrowserLanguage = (fallback = "en") => {
  if (typeof navigator === "undefined") return fallback;
  const candidates = Array.isArray(navigator.languages) ? navigator.languages : [];
  return pickSupportedLanguage([...candidates, navigator.language], fallback);
};

export const getStoredUiLanguage = () => {
  if (typeof window === "undefined") return "";
  try {
    const raw = window.localStorage.getItem(UI_LANGUAGE_STORAGE_KEY);
    const normalized = normalizeLanguageCode(raw);
    return normalized && isSupportedLanguageCode(normalized) ? normalized : "";
  } catch {
    return "";
  }
};

export const getInitialUiLanguage = (fallback = "en") => {
  const stored = getStoredUiLanguage();
  if (stored) return stored;
  return detectBrowserLanguage(fallback);
};

export const setStoredUiLanguage = (code: string) => {
  const normalized = normalizeLanguageCode(code);
  if (!normalized || !isSupportedLanguageCode(normalized) || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(UI_LANGUAGE_STORAGE_KEY, normalized);
  } catch {
    // ignore storage errors
  }
};

export const applyDocumentLanguage = (code: string) => {
  if (typeof document === "undefined") return;
  const normalized = normalizeLanguageCode(code) || "en";
  document.documentElement.lang = normalized;
  document.documentElement.dir = isRtlLanguage(normalized) ? "rtl" : "ltr";
};
